// ---------------------------------------------------------------------------
// A price box, in lira - the ONE way a whole-lira figure is typed.
//
// What is typed is what is held: digits, to the pound. The box only presents
// them - grouping the thousands (1,300,000) while the person types, and
// keeping the caret where they are typing while the commas move underneath
// it. Nothing is converted, divided or rounded on the way in or out; the
// figure the form holds is a plain digit string, and that is what is sent.
//
// It used to live inside the product editor. It moved here so the quick
// pricing list, the offer price and every future lira box are the same box:
// same grouping, same digits-only rule, same caret behaviour.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import clsx from 'clsx';
import { useI18n } from '../../i18n/I18nProvider';
import { groupThousands, LBP_LABEL } from '../../utils/format';

/// Twelve digits of lira is about eleven million dollars. Past that the
/// column would refuse it anyway, and the box stops being readable.
export const MAX_LBP_DIGITS = 12;

/// Whole lira from whatever was typed or pasted: `1,300,000`, `1300000` and
/// `1 300 000` all come back as `1300000`. Anything that is not a digit is
/// presentation, and presentation is not kept.
export function liraDigits(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, MAX_LBP_DIGITS);
}

/// The position in `grouped` that sits just after its `count`-th digit.
///
/// Retyping the contents of a box moves the caret to the end unless somebody
/// puts it back, and a price box that jumps to the end on every keystroke
/// cannot be corrected in the middle - which is exactly where a wrong digit
/// in 1,600,000 always is.
export function caretAfterDigits(grouped: string, count: number): number {
  if (count <= 0) return 0;

  let seen = 0;
  for (let at = 0; at < grouped.length; at += 1) {
    if (/\d/.test(grouped[at] as string)) {
      seen += 1;
      if (seen === count) return at + 1;
    }
  }
  return grouped.length;
}

interface LbpMoneyInputProps {
  id?: string;
  /// Whole lira, as a plain digit string.
  value: string;
  /// Called with whole lira. Nothing is converted on the way out.
  onChange: (next: string) => void;
  invalid?: boolean;
  disabled?: boolean;
  /// Whether to print the currency beside the box. Off in a dense list
  /// whose column heading already says it.
  showUnit?: boolean;
  className?: string;
  placeholder?: string;
  'aria-label'?: string;
  onBlur?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  /// The element, for a caller that moves focus between boxes.
  inputRef?: (element: HTMLInputElement | null) => void;
}

export function LbpMoneyInput({
  id,
  value,
  onChange,
  invalid = false,
  disabled,
  showUnit = true,
  className,
  placeholder = '0',
  'aria-label': ariaLabel,
  onBlur,
  onKeyDown,
  inputRef,
}: LbpMoneyInputProps) {
  const { locale } = useI18n();
  const [text, setText] = useState(() => (value ? groupThousands(value) : ''));

  // Read inside an effect that must not re-run when it changes.
  const textRef = useRef(text);
  textRef.current = text;

  const element = useRef<HTMLInputElement | null>(null);
  const caret = useRef<number | null>(null);

  // Re-group ONLY when the value changed somewhere else - a product being
  // loaded into the form, a save confirmed from the server. Comparing digits
  // rather than text is what keeps this from fighting the person typing.
  useEffect(() => {
    if (textRef.current.replace(/\D/g, '') === value) return;
    setText(value ? groupThousands(value) : '');
  }, [value]);

  // Put the caret back where the person left it, counted in DIGITS so the
  // commas moving underneath it do not drag it about.
  useEffect(() => {
    if (caret.current === null) return;
    element.current?.setSelectionRange(caret.current, caret.current);
    caret.current = null;
  });

  return (
    <div className={clsx('flex items-center gap-2', className)}>
      <input
        id={id}
        ref={(node) => {
          element.current = node;
          inputRef?.(node);
        }}
        type="text"
        inputMode="numeric"
        dir="ltr"
        value={text}
        disabled={disabled}
        aria-label={ariaLabel}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onChange={(event) => {
          const raw = event.target.value;
          const at = event.target.selectionStart ?? raw.length;

          // Digits only. There is no half lira in circulation, so a decimal
          // point in this box could only ever be a mistake - and a comma is
          // ours to draw, not the person's to keep.
          const digits = liraDigits(raw);
          const before = raw.slice(0, at).replace(/\D/g, '').length;
          const grouped = digits === '' ? '' : groupThousands(digits);

          setText(grouped);
          caret.current = caretAfterDigits(grouped, Math.min(before, digits.length));

          // Digits, exactly as typed. No division, no rounding, nothing to
          // come back a few pounds away from what the owner meant.
          onChange(digits);
        }}
        className={clsx('input numeric flex-1', invalid && 'input-error')}
        placeholder={placeholder}
      />
      {showUnit ? <span className="shrink-0 text-sm text-ink-muted" dir="ltr">{LBP_LABEL[locale]}</span> : null}
    </div>
  );
}
